import { HandlerEvent } from 'post-robot';
import { error, ValidationError } from 'common/errors';
import completeCheckoutLegacy from 'communicator/external/checkout/integrated/legacy/complete-checkout-legacy';
import {
  getAccessToken,
  getApiKey,
  getCheckoutData,
  getVIntentId,
  getMeta,
  getAssuranceDataForThreeDs,
  getSelectCardPutIntentPayload,
  getPutIntentResponse
} from 'communicator/store/selectors';
import { fetchPutIntent } from 'communicator/utils/api/intent';
import { markStart, markEnd } from 'communicator/utils/instrumentation';
import {
  cvvLaunchHandler,
  dcfLaunchHandler,
  fidoLaunchHandler,
  launchIssuerOtpFlowHandler,
  srcOrchestrateBankAppHandler,
  threeDsLaunchHandler,
  vppLaunchHandler
} from 'communicator/utils/launch-handler-functions';
import { CheckoutCommunicatorPayload, LaunchHandlers } from 'types/visa-sdk/checkout';

type CheckoutDataThreeDs = {
  data: { status: string };
  launchHandlers: LaunchHandlers;
};

export default async function threeDsComplete(
  event: HandlerEvent<CheckoutDataThreeDs>
): Promise<CheckoutCommunicatorPayload | ValidationError> {
  const vIntentId = getVIntentId();
  const checkoutData = getCheckoutData(); // stored when communicator received the checkoutData
  const cardIdMapping = getMeta().cardIdMapping;

  if (!checkoutData) {
    return error('CHECKOUT_FAILURE');
  }

  let selectedCardId;

  if (checkoutData.srcDigitalCardId) {
    selectedCardId = cardIdMapping[checkoutData.srcDigitalCardId] ?? checkoutData.srcDigitalCardId;
  }

  if (!selectedCardId || !vIntentId) {
    markEnd('CHECKOUT_INTEGRATED', {
      doFlush: true,
      failure: true,
      reason: 'Unable to connect to DCF'
    });

    return error('Unable to connect to DCF');
  }

  const accessToken = getAccessToken()?.token as string;

  // construct the assurance data object for 3DS result received
  const threeDsCompletionData = event.data.data;
  const assuranceDataFor3DS = getAssuranceDataForThreeDs(threeDsCompletionData.status);

  const selectCardPutIntentPayload = await getSelectCardPutIntentPayload(selectedCardId);

  selectCardPutIntentPayload.assuranceData = assuranceDataFor3DS;
  delete selectCardPutIntentPayload.authenticationContext;
  delete selectCardPutIntentPayload.authenticationMethod;

  markStart('checkout_integrated_put_intent');

  const sessionQualifier = getPutIntentResponse()?.headers['x-iam-session-qualifier'];

  const putIntentResponse = await fetchPutIntent({
    accessToken,
    apikey: getApiKey(),
    requestPayload: selectCardPutIntentPayload,
    sessionQualifier,
    vIntentId,
    vsbFlow: checkoutData.vsbFlow || false
  });

  markEnd('checkout_integrated_put_intent', {
    failure: !putIntentResponse.ok,
    reason: putIntentResponse.ok ? undefined : 'CHECKOUT_FAILURE'
  });

  const checkoutResponse = await completeCheckoutLegacy(
    { checkoutData, putIntentResponse },
    srcOrchestrateBankAppHandler(event.data.launchHandlers.launchSRCBankApp),
    launchIssuerOtpFlowHandler(event.data.launchHandlers.launchSRCIssuerOtp),
    cvvLaunchHandler(event.data.launchHandlers.launchCvv),
    dcfLaunchHandler(event.data.launchHandlers.launchDcf),
    fidoLaunchHandler(event.data.launchHandlers.launchFIDO),
    threeDsLaunchHandler(event.data.launchHandlers.launchThreeDs),
    threeDsLaunchHandler(event.data.launchHandlers.launchThreeDsWithOnLoad),
    vppLaunchHandler(event.data.launchHandlers.launchVPP)
  );

  if (checkoutResponse.error) {
    markEnd('CHECKOUT_INTEGRATED', {
      doFlush: true,
      failure: true,
      reason: 'CHECKOUT_FAILURE'
    });
  } else {
    markEnd('CHECKOUT_INTEGRATED', { doFlush: true });
  }

  return checkoutResponse;
}
